import { useEffect, useState } from 'react'
import AnimationLayout from './components/AnimationLayout'

interface Badge {
  name: string
  emoji: string
  description: string
  points_awarded: number
}

interface Props {
  animation: { id: number; name: string; description: string }
  badge: Badge
  all_animations: { id: number; name: string }[]
}

export default function Animation31({ animation, badge, all_animations }: Props) {
  const [pieces, setPieces] = useState([false, false, false, false])
  const [showComplete, setShowComplete] = useState(false)

  useEffect(() => {
    // Assembler les pièces une par une
    setTimeout(() => setPieces([true, false, false, false]), 300)
    setTimeout(() => setPieces([true, true, false, false]), 700)
    setTimeout(() => setPieces([true, true, true, false]), 1100)
    setTimeout(() => setPieces([true, true, true, true]), 1500)
    setTimeout(() => setShowComplete(true), 2000)
  }, [])

  return (
    <AnimationLayout animation={animation} allAnimations={all_animations}>
      <style>{`
        @keyframes pieceSlideIn0 {
          from { transform: translate(-100px, -100px) rotate(-45deg); opacity: 0; }
          to { transform: translate(0, 0) rotate(0deg); opacity: 1; }
        }
        @keyframes pieceSlideIn1 {
          from { transform: translate(100px, -100px) rotate(45deg); opacity: 0; }
          to { transform: translate(0, 0) rotate(0deg); opacity: 1; }
        }
        @keyframes pieceSlideIn2 {
          from { transform: translate(-100px, 100px) rotate(45deg); opacity: 0; }
          to { transform: translate(0, 0) rotate(0deg); opacity: 1; }
        }
        @keyframes pieceSlideIn3 {
          from { transform: translate(100px, 100px) rotate(-45deg); opacity: 0; }
          to { transform: translate(0, 0) rotate(0deg); opacity: 1; }
        }
        .piece-0 { animation: pieceSlideIn0 0.4s ease-out forwards; }
        .piece-1 { animation: pieceSlideIn1 0.4s ease-out forwards; }
        .piece-2 { animation: pieceSlideIn2 0.4s ease-out forwards; }
        .piece-3 { animation: pieceSlideIn3 0.4s ease-out forwards; }
        @keyframes glowPulse {
          0%, 100% { box-shadow: 0 0 20px rgba(255, 241, 152, 0.5); }
          50% { box-shadow: 0 0 40px rgba(255, 241, 152, 0.8); }
        }
        .glow-pulse { animation: glowPulse 1.5s ease-in-out infinite; }
      `}</style>

      <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh]">
        {/* Puzzle badge */}
        <div className="relative w-32 h-32 sm:w-44 sm:h-44">
          {/* Les 4 pièces du puzzle */}
          <div className={`absolute top-0 left-0 w-1/2 h-1/2 ${pieces[0] ? 'piece-0' : 'opacity-0'}`}>
            <div className="w-full h-full bg-tessan-cream rounded-tl-2xl flex items-end justify-end overflow-hidden">
              <span className="text-4xl transform translate-x-1/2 translate-y-1/2">{badge.emoji}</span>
            </div>
          </div>
          <div className={`absolute top-0 right-0 w-1/2 h-1/2 ${pieces[1] ? 'piece-1' : 'opacity-0'}`}>
            <div className="w-full h-full bg-tessan-cream rounded-tr-2xl flex items-end justify-start overflow-hidden">
              <span className="text-4xl transform -translate-x-1/2 translate-y-1/2">{badge.emoji}</span>
            </div>
          </div>
          <div className={`absolute bottom-0 left-0 w-1/2 h-1/2 ${pieces[2] ? 'piece-2' : 'opacity-0'}`}>
            <div className="w-full h-full bg-tessan-cream rounded-bl-2xl flex items-start justify-end overflow-hidden">
              <span className="text-4xl transform translate-x-1/2 -translate-y-1/2">{badge.emoji}</span>
            </div>
          </div>
          <div className={`absolute bottom-0 right-0 w-1/2 h-1/2 ${pieces[3] ? 'piece-3' : 'opacity-0'}`}>
            <div className="w-full h-full bg-tessan-cream rounded-br-2xl flex items-start justify-start overflow-hidden">
              <span className="text-4xl transform -translate-x-1/2 -translate-y-1/2">{badge.emoji}</span>
            </div>
          </div>

          {/* Badge complet qui apparaît par-dessus */}
          {showComplete && (
            <div className="absolute inset-0 w-32 h-32 sm:w-44 sm:h-44 bg-tessan-cream rounded-2xl flex items-center justify-center glow-pulse">
              <span className="text-6xl sm:text-8xl">{badge.emoji}</span>
            </div>
          )}
        </div>

        <div className={`mt-8 text-center transition-all duration-700 ${showComplete ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="text-2xl sm:text-4xl font-black text-tessan-yellow">{badge.name}</div>
          <div className="text-lg sm:text-xl text-tessan-cream mt-2">+{badge.points_awarded} points</div>
          <div className="text-tessan-cream/70 mt-4 max-w-[280px] sm:max-w-xs">{badge.description}</div>
        </div>
      </div>
    </AnimationLayout>
  )
}
